import { useLazyQuery } from '@collinsonx/utils/apollo';
import {
  VisitorInputData,
  VisitorType,
} from '@collinsonx/utils/generatedTypes/graphql';
import { getProductValuation } from '@collinsonx/utils/queries';
import {
  OriginType,
  sendErrorLog,
  sendInfoLog,
  serializeError,
} from '@lib/logging';
import { BookingError, BookingInfo } from 'config/datadog';
// TODO(TS-XXX / cleanup sub-task of TS-106): once valuation pricing is the
// only path, delete the `shouldUseValuationPricing` guard, remove the
// `if (!useValuationOnly) return NULL_RESULT` guard and the
// `LOADING_RESULT` init branch below, and always run the fetch
import { shouldUseValuationPricing } from 'config/features';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LangObj } from 'types/language';
import {
  ProductValuationType,
  ValuationPriceBreakdown,
} from 'types/mappedProductData';
import { getCurrencySymbol } from 'utils/currencysymbol';

import useBooking from './useBooking';
import useError from './useError';
import useLocale from './useLocale';
import usePayload from './usePayload';
import useProduct from './useProduct';

const DEBOUNCE_MS = 250;
const MAX_ATTEMPTS = 2; // one initial attempt + one retry

const isValidPrice = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v);

interface ValuationPriceInput {
  adults: number;
  children: number;
  infants: number;
  // When true, the outgoing `entitlementSelection` is always pinned to
  // `{ numOfEntitlementsToUse: 0 }` and the hook stops reacting to
  // `selectedEntitlementsCount`, so the fetch only fires once per
  // hydration. Used by the LoungeInfo per-adult price call, which must
  // be entitlement-agnostic.
  ignoreEntitlements?: boolean;
}

interface ValuationPriceResult {
  coveredByEntitlement: (boolean | null)[] | null;
  currency: string | null;
  error: boolean;
  loading: boolean;
  memberPrice: number | null;
  memberPriceFormatted: string | null;
  priceBreakdown: (ValuationPriceBreakdown | null)[] | null;
  totalPrice: number | null;
  totalPriceFormatted: string | null;
}

const NULL_RESULT: ValuationPriceResult = {
  coveredByEntitlement: null,
  currency: null,
  error: false,
  loading: false,
  memberPrice: null,
  memberPriceFormatted: null,
  priceBreakdown: null,
  totalPrice: null,
  totalPriceFormatted: null,
};

const LOADING_RESULT: ValuationPriceResult = {
  ...NULL_RESULT,
  loading: true,
};

// Pure, type-agnostic: consumes the entitlement budget positionally, so
// callers must pass `visitors` already ordered by priority (primary
// member first, then other adults, then children, then infants).
export const assignEntitlements = (
  visitors: VisitorInputData[],
  entitlementsCount: number
): VisitorInputData[] => {
  let remaining = Math.max(entitlementsCount, 0);

  return visitors.map((visitor) => {
    if (remaining > 0) {
      remaining -= 1;
      return { ...visitor, useEntitlement: true };
    }
    return { ...visitor, useEntitlement: false };
  });
};

export const buildVisitors = (
  adults: number,
  children: number,
  infants: number,
  entitlementsCount: number
): VisitorInputData[] => {
  const visitors: VisitorInputData[] = [
    { isGuest: false, visitorType: VisitorType.Adult },
  ];

  for (let i = 1; i < adults; i++) {
    visitors.push({ isGuest: true, visitorType: VisitorType.Adult });
  }

  for (let i = 0; i < children; i++) {
    visitors.push({ isGuest: true, visitorType: VisitorType.Child });
  }

  for (let i = 0; i < infants; i++) {
    visitors.push({ isGuest: true, visitorType: VisitorType.Infant });
  }

  return assignEntitlements(visitors, entitlementsCount);
};

// Prefer Membership.id (GraphQL ID). Do not use LinkedAccount.membershipID —
// that field is a membership number, not the valuation membershipID.
export const firstMembershipId = (
  memberships?: Array<{ id?: string | null } | null> | null
): string | undefined => memberships?.find((m) => m?.id)?.id ?? undefined;

type FetchValuationFn = ReturnType<
  typeof useLazyQuery<{ getProductValuation: ProductValuationType }>
>[0];

type LatestValues = {
  adults: number;
  children: number;
  infants: number;
  selectedEntitlementsCount: number;
  ignoreEntitlements: boolean;
  consumerID: string | undefined;
  resolvedMembershipID: string | undefined;
  productID: string | undefined;
  currency: string | undefined;
  translation: LangObj;
  setError: ReturnType<typeof useError>['setError'];
  fetchProductValuation: FetchValuationFn;
};

const useValuationPrice = ({
  adults,
  children,
  infants,
  ignoreEntitlements = false,
}: ValuationPriceInput): ValuationPriceResult => {
  const {
    consumerData,
    consumerID,
    linkedAccountId,
    matchedAccount,
    membershipID,
    payload,
  } = usePayload();
  const { category, currency, loadingProduct, productID } = useProduct();
  const { booking } = useBooking();
  const { selectedEntitlementsCount } = booking;
  const { setError } = useError();
  const translation = useLocale();

  const useValuationOnly = shouldUseValuationPricing(category);

  const resolvedMembershipID = useMemo(() => {
    if (membershipID) return membershipID;

    const fromMatchedAccount = firstMembershipId(matchedAccount?.memberships);
    if (fromMatchedAccount) return fromMatchedAccount;

    if (linkedAccountId) {
      const linkedAccount = consumerData?.getConsumerByID?.linkedAccounts?.find(
        (account) => account?.id === linkedAccountId
      );
      const fromLinkedAccount = firstMembershipId(linkedAccount?.memberships);
      if (fromLinkedAccount) return fromLinkedAccount;
    }

    return firstMembershipId(consumerData?.getConsumerByID?.memberships);
  }, [membershipID, matchedAccount, linkedAccountId, consumerData]);

  // Hydration signal: true while payload / consumerData / product are still
  // resolving on the initial page load. We use this to suppress the
  // deps-missing pricing error until hydration has settled, so we don't
  // flash `LayoutError` on the very first paint before the async data
  // has arrived.
  const isHydrating = !payload || !consumerData || loadingProduct;

  // Start in `loading` when the flag is ON so `LoungeInfo` / `TotalPriceBlock`
  // render their skeleton while payload / product hydration is in flight,
  // instead of silently rendering nothing (empty NULL_RESULT).
  const [result, setResult] = useState<ValuationPriceResult>(() =>
    useValuationOnly ? LOADING_RESULT : NULL_RESULT
  );

  const [fetchProductValuation] = useLazyQuery<{
    getProductValuation: ProductValuationType;
  }>(getProductValuation, {
    fetchPolicy: 'network-only',
  });

  // Every value the async worker needs is captured in a ref so the worker
  // stays a stable `useCallback` (`[]` deps). This prevents identity churn
  // in the trigger effect below.
  const latestRef = useRef<LatestValues>({
    adults,
    children,
    infants,
    selectedEntitlementsCount,
    ignoreEntitlements,
    consumerID,
    resolvedMembershipID,
    productID,
    currency,
    translation,
    setError,
    fetchProductValuation,
  });

  useEffect(() => {
    latestRef.current = {
      adults,
      children,
      infants,
      selectedEntitlementsCount,
      ignoreEntitlements,
      consumerID,
      resolvedMembershipID,
      productID,
      currency,
      translation,
      setError,
      fetchProductValuation,
    };
  });

  const requestCounterRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ownership flag: true when this hook is the one holding the pricing
  // error on the global DomainContext. We only clear the context error if
  // we set it, so we don't accidentally clear a different error set by
  // another hook.
  const didFireOursRef = useRef(false);

  const fireDepsUnavailableError = useCallback(() => {
    const state = latestRef.current;
    const missing: string[] = [];
    if (!state.consumerID) missing.push('consumerID');
    if (!state.productID) missing.push('productID');

    const reason = new Error(`Valuation deps missing: ${missing.join(', ')}`);

    sendErrorLog(BookingError.ERR_SOMETHING_WENT_WRONG, {
      error: serializeError(reason),
      missing,
      origin: OriginType.API,
    });

    setResult({ ...NULL_RESULT, error: true });
    state.setError({
      key: BookingError.ERR_SOMETHING_WENT_WRONG,
      message: state.translation.generic.error.pricingError.message,
      title: state.translation.generic.error.pricingError.title,
    });
  }, []);

  const runValuation = useCallback(async () => {
    const state = latestRef.current;

    // membershipID is optional (linked users may not have one). Only
    // consumerID + productID are hard requirements for the valuation call.
    if (!state.consumerID || !state.productID) {
      fireDepsUnavailableError();
      didFireOursRef.current = true;
      return;
    }

    const currentRequest = ++requestCounterRef.current;

    const numOfEntitlementsToUse = state.ignoreEntitlements
      ? 0
      : state.selectedEntitlementsCount > 0
      ? state.selectedEntitlementsCount
      : 0;

    const entitlementSelection = {
      numOfEntitlementsToUse,
    };

    const visitors = buildVisitors(
      state.adults,
      state.children,
      state.infants,
      numOfEntitlementsToUse
    );

    const valuationInput = {
      consumerID: state.consumerID,
      entitlementSelection,
      productID: state.productID,
      visitors,
      ...(state.resolvedMembershipID && {
        membershipID: state.resolvedMembershipID,
      }),
    };
    const variables = { valuationInput };

    const currencySymbol = getCurrencySymbol(state.currency ?? '');
    const formatPrice = (price: number) =>
      `${currencySymbol}${price.toFixed(2)}`;

    let lastError: unknown = null;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      if (currentRequest !== requestCounterRef.current) return;

      let data: { getProductValuation: ProductValuationType } | null = null;
      let error: unknown = null;

      try {
        const response = await state.fetchProductValuation({ variables });
        data = response.data ?? null;
        error = response.error ?? null;
      } catch (thrown) {
        error = thrown;
      }

      if (currentRequest !== requestCounterRef.current) return;

      if (error) {
        lastError = error;
        continue;
      }

      const priceDetails = data?.getProductValuation?.priceDetails;

      if (!priceDetails) {
        lastError = new Error('Empty valuation response');
        continue;
      }

      if (!isValidPrice(priceDetails.finalPrice)) {
        lastError = new Error('Valuation response missing finalPrice');
        continue;
      }

      sendInfoLog(BookingInfo.VALUATION_PRODUCT_RESPONSE, {
        attempt,
        valuationInput,
        valuationResponse: data?.getProductValuation,
      });

      setResult({
        coveredByEntitlement: priceDetails.coveredByEntitlement ?? null,
        currency: priceDetails.currency ?? state.currency ?? null,
        error: false,
        loading: false,
        memberPrice: isValidPrice(priceDetails.memberPrice)
          ? priceDetails.memberPrice
          : null,
        memberPriceFormatted: isValidPrice(priceDetails.memberPrice)
          ? formatPrice(priceDetails.memberPrice)
          : null,
        priceBreakdown: priceDetails.priceBreakdown ?? null,
        totalPrice: priceDetails.finalPrice,
        totalPriceFormatted: formatPrice(priceDetails.finalPrice),
      });
      return;
    }

    if (currentRequest !== requestCounterRef.current) return;

    sendErrorLog(BookingError.ERR_SOMETHING_WENT_WRONG, {
      error: serializeError(lastError),
      origin: OriginType.API,
      valuationInput,
    });

    setResult({ ...NULL_RESULT, error: true });
    state.setError({
      key: BookingError.ERR_SOMETHING_WENT_WRONG,
      message: state.translation.generic.error.pricingError.message,
      title: state.translation.generic.error.pricingError.title,
    });
  }, []);

  useEffect(() => {
    if (!useValuationOnly) return;

    // Still bringing payload / consumerData / product online — treat as
    // loading, not as a missing-deps error. Prevents the LayoutError flash
    // on the very first paint before hydration completes.
    if (isHydrating) {
      setResult((prev) => (prev.loading ? prev : LOADING_RESULT));
      return;
    }

    if (!consumerID || !productID) {
      // Deps missing after hydration settled — surface the pricing error immediately.
      // membershipID is optional for linked users and must not block pricing.
      fireDepsUnavailableError();
      didFireOursRef.current = true;
      return;
    }

    // Deps arrived — clear the pricing error we may have set on a prior
    // effect run so healthy loads don't stay stuck on `LayoutError`.
    if (didFireOursRef.current) {
      didFireOursRef.current = false;
      latestRef.current.setError(null);
    }

    setResult((prev) => ({ ...prev, loading: true, error: false }));

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(runValuation, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [
    useValuationOnly,
    isHydrating,
    consumerID,
    resolvedMembershipID,
    productID,
    adults,
    children,
    infants,
    // When `ignoreEntitlements` is set, entitlement values are pinned to
    // `numOfEntitlementsToUse: 0`, so we deliberately drop them from the
    // trigger deps to keep the fetch stable across booking-driven
    // entitlement changes.
    ignoreEntitlements ? 0 : selectedEntitlementsCount,
    ignoreEntitlements,
    runValuation,
    fireDepsUnavailableError,
  ]);

  if (!useValuationOnly) return NULL_RESULT;

  return result;
};

export default useValuationPrice;
